/**
 * Footer — Redesigned brand footer.
 * Styled in theme's Olive/Sky Reflection gradient with Champagne details.
 * Includes validated newsletter subscription form and simulated Instagram device widget with details lightbox.
 * @module shared/ui/Footer
 */
import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import { getImageUrl } from '@/shared/utils/imageUrl';

import { Camera, MapPin, Mail, Phone, ArrowRight, Play, Heart, Instagram, Facebook, Youtube, X, ChevronLeft, ChevronRight, Volume2, VolumeX, Check, Sparkles, Loader2 } from 'lucide-react';
import { subscribeNewsletter } from '../api/newsletter';
import { getInstagramPosts, getInstagramCount } from '../api/instagram';

import { useAppStore } from '@/shared/store/appStore';
import { useToast } from '@/shared/ui/Toast';
import CinescopeLogo from './Navbar/CinescopeLogo';
import Turnstile from '@/shared/ui/Turnstile';

export default function Footer() {
  const [newsletterEmail, setNewsletterEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSubscribeSuccess, setShowSubscribeSuccess] = useState(false);
  const [newsletterError, setNewsletterError] = useState(null);
  const [turnstileToken, setTurnstileToken] = useState('');
  const { showToast } = useToast();
  
  const [selectedInstaPost, setSelectedInstaPost] = useState(null);
  const [isFollowed, setIsFollowed] = useState(false);
  const [isMuted, setIsMuted] = useState(true);

  const demoMode = useAppStore((state) => state.demoMode);
  const [instagramPosts, setInstagramPosts] = useState([]);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const PAGE_SIZE = 9;
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const apiBaseUrl = import.meta.env.VITE_API_URL || 'http://localhost:8000';


  const formatDisplayDate = (dateStr) => {
    if (!dateStr) return '';
    const match = dateStr.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (match) {
      const [_, year, month, day] = match;
      const months = [
        'January', 'February', 'March', 'April', 'May', 'June',
        'July', 'August', 'September', 'October', 'November', 'December'
      ];
      const monthName = months[parseInt(month, 10) - 1] || month;
      return `${parseInt(day, 10)} ${monthName} ${year}`;
    }
    return dateStr;
  };

  useEffect(() => {
    setCurrentImageIndex(0);
  }, [selectedInstaPost]);

  const footerRef = useRef(null);
  const [isNearFooter, setIsNearFooter] = useState(false);

  useEffect(() => {
    if (!footerRef.current) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsNearFooter(true);
        }
      },
      { rootMargin: '600px 0px' }
    );
    observer.observe(footerRef.current);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!isNearFooter) return;
    let mounted = true;
    // Load initial page
    getInstagramPosts(PAGE_SIZE, 0)
      .then((data) => {
        if (!mounted) return;
        if (data && Array.isArray(data) && data.length > 0) {
          setInstagramPosts(data);
        } else {
          setInstagramPosts([]);
        }
      })
      .catch(() => {
        if (mounted) setInstagramPosts([]);
      });
    // Fetch total count separately for Load More button
    getInstagramCount()
      .then((res) => {
        if (mounted) setTotalCount(res?.total ?? 0);
      })
      .catch(() => {
        if (mounted) setTotalCount(0);
      });

    return () => {
      mounted = false;
    };
  }, [isNearFooter]);

  /**
   * Load the next batch of Instagram posts and append to the current list.
   */
  const handleLoadMore = async () => {
    if (isLoadingMore) return;
    setIsLoadingMore(true);
    try {
      const more = await getInstagramPosts(PAGE_SIZE, instagramPosts.length);
      if (more && more.length > 0) {
        setInstagramPosts((prev) => [...prev, ...more]);
      }
    } catch (err) {
      // fail silently — grid remains as-is
    } finally {
      setIsLoadingMore(false);
    }
  };


  const EMAIL_REGEX = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

  const handleSubscribe = async (e) => {
    e.preventDefault();
    setNewsletterError(null);

    if (!newsletterEmail.trim()) {
      setNewsletterError('Email address is required.');
      return;
    }

    if (!EMAIL_REGEX.test(newsletterEmail.trim())) {
      setNewsletterError('Please enter a valid email address.');
      return;
    }

    if (!turnstileToken) {
      setNewsletterError('Please complete the verification.');
      return;
    }

    setIsSubmitting(true);
    try {
      await subscribeNewsletter(newsletterEmail.trim(), turnstileToken);
      setShowSubscribeSuccess(true);
      showToast('Verification email sent! Please check your inbox.', 'success');
      setNewsletterEmail('');
      setTurnstileToken(''); // Clear token
    } catch (err) {
      const detail = err.response?.data?.detail || 'Failed to subscribe. Please try again.';
      setNewsletterError(detail);
      showToast(detail, 'error');
    } finally {
      setIsSubmitting(false);
    }
  };


  const selectedPostData = instagramPosts.find(p => p.id === selectedInstaPost);

  return (
    <footer ref={footerRef} className="bg-gradient-to-r from-[#2b2344] via-[#1a152e] to-[#2b2344] border-t border-white/10 pt-20 pb-10 relative overflow-hidden w-full text-left" id="home-page-footer">
      
      {/* Background Ambient Glows */}
      <div className="absolute top-0 left-1/4 w-96 h-96 bg-white/5 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-[#793bfb]/5 rounded-full blur-3xl pointer-events-none" />

      <div className="max-w-7xl mx-auto px-6 relative z-10">
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-12 items-start">
          
          {/* Column 1: Brand Info & Social Badges */}
          <div className="lg:col-span-3 space-y-6">
            <CinescopeLogo textColor="text-white" showSubtitle={true} />

            <div className="space-y-4 text-xs text-zinc-200 font-sans leading-relaxed">
              <p>
                We offer some of the finest ever made gears with a depth of experienced technicians at amazingly affordable prices.
              </p>
              <p>
                Don't shy away with your creative ideas &amp; unimaginable requirements.
              </p>
              <p>
                We often surprise our clients with unbelievable solutions.
              </p>
            </div>

            {/* Direct Connection Nodes */}
            <div className="space-y-2 pt-2">
              <span className="text-[8px] font-mono text-zinc-300 uppercase tracking-widest block">Direct Connection Nodes:</span>
              <div className="flex flex-wrap gap-2.5">
                {[
                  { 
                    icon: (
                      <svg className="w-4 h-4 fill-current" viewBox="0 0 448 512" aria-hidden="true">
                        <path d="M380.9 97.1C339 55.1 283.2 32 223.9 32c-122.4 0-222 99.6-222 222 0 39.1 10.2 77.3 29.6 111L0 480l117.7-30.9c32.4 17.7 68.9 27 106.1 27h.1c122.3 0 224.1-99.6 224.1-222 0-59.3-25.2-115-67.1-157zm-157 341.6c-33.2 0-65.7-8.9-94-25.7l-6.7-4-69.8 18.3L72 359.2l-4.4-7c-18.5-29.4-28.2-63.3-28.2-98.2 0-101.7 82.8-184.5 184.6-184.5 49.3 0 95.6 19.2 130.4 54.1 34.8 34.9 56.2 81.2 56.1 130.5 0 101.8-84.9 184.6-186.6 184.6zm101.2-138.2c-5.5-2.8-32.8-16.2-37.9-18-5.1-1.9-8.8-2.8-12.5 2.8-3.7 5.6-14.3 18-17.6 21.8-3.2 3.7-6.5 4.2-12 1.4-32.6-16.3-54-29.1-75.5-66-5.7-9.8 5.7-9.1 16.3-30.3 1.8-3.7.9-6.9-.5-9.7-1.4-2.8-12.5-30.1-17.1-41.2-4.5-10.8-9.1-9.3-12.5-9.5-3.2-.2-6.9-.2-10.6-.2-3.7 0-9.7 1.4-14.8 6.9-5.1 5.6-19.4 19-19.4 46.3 0 27.3 19.9 53.7 22.6 57.4 2.8 3.7 39.1 59.7 94.8 83.8 35.2 15.2 49 16.5 66.6 13.9 10.7-1.6 32.8-13.4 37.4-26.4 4.6-13 4.6-24.1 3.2-26.4-1.3-2.5-5-3.9-10.5-6.6z" />
                      </svg>
                    ), 
                    color: "hover:bg-white/15 hover:text-white hover:border-white/40", 
                    name: "WhatsApp",
                    href: "https://wa.me/919624545785" 
                  },
                  { 
                    icon: <Instagram className="w-4 h-4" />, 
                    color: "hover:bg-white/15 hover:text-white hover:border-white/40", 
                    name: "Instagram",
                    href: "https://www.instagram.com/cinescope.co.in" 
                  },
                  { 
                    icon: <Facebook className="w-4 h-4" />, 
                    color: "hover:bg-white/15 hover:text-white hover:border-white/40", 
                    name: "Facebook",
                    href: "https://www.facebook.com/cinescope.co.in/" 
                  },
                  { 
                    icon: <Youtube className="w-4 h-4" />, 
                    color: "hover:bg-white/15 hover:text-white hover:border-white/40", 
                    name: "YouTube",
                    href: "https://www.youtube.com/@cinescopeindia9523" 
                  }
                ].map((soc, idx) => (
                  <a
                    key={idx}
                    href={soc.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={soc.name}
                    title={soc.name}
                    className={`w-9 h-9 rounded-xl bg-white/5 border border-white/15 flex items-center justify-center text-zinc-200 transition-all duration-300 hover:scale-110 hover:-translate-y-0.5 active:scale-95 ${soc.color}`}
                  >
                    {soc.icon}
                  </a>
                ))}
              </div>
            </div>
          </div>

          {/* Column 2: Benefits list */}
          <div className="lg:col-span-3 space-y-6">
            <div className="border-b border-white/10 pb-2">
              <p className="font-sans font-bold text-xs text-[#793bfb] uppercase tracking-wider flex items-center gap-2">
                <span className="w-1.5 h-1.5 bg-[#793bfb] rounded-full animate-pulse" />
                <span>Benefits with Us</span>
              </p>
            </div>

            <div className="flex flex-col gap-3 font-sans">
              {[
                "We are available.",
                "We keep promises.",
                "We sustain regardless.",
                "We act fast with/against the market.",
                "We deliver."
              ].map((benefit, idx) => (
                <div 
                  key={idx}
                  className="flex items-center gap-3 text-zinc-200 hover:text-white transition-all duration-300 hover:translate-x-1.5 cursor-pointer group"
                >
                  <div className="w-2.5 h-2.5 rounded-full bg-[#793bfb]/30 border border-[#793bfb] flex items-center justify-center shrink-0 group-hover:bg-[#793bfb] group-hover:rotate-180 transition-all duration-500">
                    <div className="w-1 h-1 bg-white rounded-full group-hover:scale-0 transition-transform" />
                  </div>
                  <span className="text-xs tracking-wide leading-relaxed font-medium">
                    {benefit}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* Column 3: Contact Info & Newsletter subscription */}
          <div className="lg:col-span-3 space-y-6">
            <div className="border-b border-white/10 pb-2">
              <p className="font-sans font-bold text-xs text-[#793bfb] uppercase tracking-wider flex items-center gap-2">
                <span className="w-1.5 h-1.5 bg-[#793bfb] rounded-full animate-pulse" />
                <span>Contact Info</span>
              </p>
            </div>

            <div className="space-y-4 font-sans text-xs text-zinc-200">
              {/* Address */}
              <div className="flex gap-3">
                <MapPin className="w-4 h-4 text-[#793bfb] shrink-0 mt-0.5" />
                <div className="space-y-1">
                  <span className="text-[9px] font-mono text-zinc-300 uppercase tracking-widest block font-bold">Corporate Office</span>
                  <p className="leading-relaxed">
                    225, Wing A, Monalisa Business Centre, Manjalpur, Vadodara – 390011, Gujarat
                  </p>
                </div>
              </div>

              {/* Email */}
              <div className="flex gap-3 items-center">
                <Mail className="w-4 h-4 text-[#793bfb] shrink-0" />
                <div>
                  <span className="text-[9px] font-mono text-zinc-300 uppercase tracking-widest block font-bold">Email Contact</span>
                  <a href="mailto:sales@cinescope.co.in" className="hover:text-white transition-colors font-mono">
                    sales@cinescope.co.in
                  </a>
                </div>
              </div>

              {/* Hotline */}
              <div className="flex gap-3 items-center">
                <Phone className="w-4 h-4 text-[#793bfb] shrink-0" />
                <div>
                  <span className="text-[9px] font-mono text-zinc-300 uppercase tracking-widest block font-bold">Call Support</span>
                  <a href="tel:09624545785" className="hover:text-white transition-colors font-mono font-bold tracking-wider">
                    09624545785
                  </a>
                </div>
              </div>
            </div>

            {/* Newsletter Subscription */}
            <div className="space-y-2.5 pt-4 border-t border-white/10">
              <span className="text-[9px] font-mono text-zinc-300 uppercase tracking-widest block font-bold">Newsletter subscription</span>
              
              <form onSubmit={handleSubscribe} className="space-y-2" noValidate>
                <div className="flex items-center bg-white/5 border border-white/15 rounded-xl p-1.5 focus-within:border-white/40 transition-colors">
                  <input
                    type="email"
                    aria-label="Newsletter email address"
                    placeholder="Enter your email address"
                    value={newsletterEmail}
                    onChange={(e) => {
                      setNewsletterEmail(e.target.value);
                      if (newsletterError) setNewsletterError(null);
                    }}
                    className="w-full bg-transparent px-2.5 py-1 text-xs text-white focus:outline-none placeholder:text-zinc-300 font-mono"
                  />
                  <button
                    type="submit"
                    aria-label="Subscribe to newsletter"
                    disabled={isSubmitting}
                    className="bg-white hover:bg-[#793bfb] text-[#793bfb] hover:text-white p-2 rounded-lg transition-all duration-300 hover:scale-105 active:scale-95 shrink-0 cursor-pointer flex items-center justify-center"
                  >
                    <ArrowRight className="w-3.5 h-3.5" />
                  </button>
                </div>

                <Turnstile
                  onVerify={setTurnstileToken} 
                  onExpire={() => setTurnstileToken('')} 
                  onError={() => setTurnstileToken('')} 
                  />

                {newsletterError && (
                  <p className="text-rose-400 text-[10px] font-mono font-semibold pl-1.5 mt-1.5">
                    {newsletterError}
                  </p>
                )}
              </form>
            </div>
          </div>

          {/* Column 4: Simulated Live Instagram grid widget device */}
          <div className="lg:col-span-3 space-y-4">
            <div className="border-b border-white/10 pb-2">
              <p className="font-sans font-bold text-xs text-[#793bfb] uppercase tracking-wider flex items-center gap-2">
                <span className="w-1.5 h-1.5 bg-[#793bfb] rounded-full animate-pulse" />
                <span>Follow Us</span>
              </p>
            </div>

            <div className="bg-white/5 border border-white/15 rounded-2xl p-4 space-y-4 shadow-2xl relative overflow-hidden group">
              {/* Live Scanner beam simulation */}
              <div className="absolute top-0 left-0 w-full h-[1px] bg-gradient-to-r from-transparent via-[#793bfb]/40 to-transparent animate-[shimmer_4s_infinite_linear]" />
              
              {/* Account Widget Header */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-full bg-white/10 border border-white/20 flex items-center justify-center text-[10px] text-white font-bold shrink-0">
                    <span>CS</span>
                  </div>
                  <div className="flex flex-col">
                    <div className="flex items-center gap-1">
                      <span className="text-[10px] font-bold text-white font-mono lowercase">cinescope.co.in</span>
                      <span className="w-2.5 h-2.5 bg-[#793bfb] text-[#33658A] rounded-full flex items-center justify-center text-[5px] font-sans font-extrabold">✓</span>
                    </div>
                    <span className="text-[7.5px] text-zinc-200 font-mono leading-none">Specialist for 🎥 &amp; 📺 gear</span>
                  </div>
                </div>
              </div>

              {/* Instagram Feed Grid (3x3) */}
              {instagramPosts.length > 0 && (
                <div className="grid grid-cols-3 gap-1.5">
                  {instagramPosts.map((post) => (
                    <div
                      key={post.id}
                      onClick={() => setSelectedInstaPost(post.id)}
                      className="aspect-square bg-zinc-950 rounded-lg relative overflow-hidden cursor-pointer group/item border border-white/10 shadow-inner hover:scale-105 active:scale-95 transition-transform duration-200"
                    >
                      {/* Media Preview */}
                      {post.media_urls && post.media_urls.length > 0 ? (
                        post.type === 'video' ? (
                          <div className="absolute inset-0 bg-gradient-to-br from-zinc-900 via-zinc-800 to-black flex items-center justify-center">
                            <div className="w-7 h-7 rounded-full bg-white/15 flex items-center justify-center border border-white/25 text-[#793bfb] group-hover/item:scale-110 group-hover/item:bg-[#793bfb] group-hover/item:text-white transition-all">
                              <Play size={12} className="ml-0.5 fill-current" />
                            </div>
                          </div>
                        ) : (
                          <img
                            src={getImageUrl(post.media_urls[0])}
                            width="80"
                            height="80"
                            loading="lazy"
                            decoding="async"
                            className="absolute inset-0 w-full h-full object-cover z-0"
                            alt={post.title}
                          />
                        )
                      ) : (
                        <div className="absolute inset-0 flex flex-col items-center justify-center bg-white/5">
                          <div className="absolute inset-0 opacity-5 bg-[linear-gradient(to_right,#ffffff_1px,transparent_1px),linear-gradient(to_bottom,#ffffff_1px,transparent_1px)] bg-[size:10px_10px]" />
                          <span className="text-[5.5px] font-mono text-zinc-400 uppercase">POST_0{post.id}</span>
                        </div>
                      )}

                      {/* Small Type Icon Badge */}
                      <div className="absolute top-1 right-1 z-10 bg-black/50 backdrop-blur-xs p-1 rounded text-white/90">
                        {post.type === 'video' ? <Play size={8} /> : <Camera size={8} />}
                      </div>

                      {/* Hover Overlay details */}
                      <div className="absolute inset-0 bg-[#793bfb]/90 opacity-0 group-hover/item:opacity-100 transition-opacity duration-200 flex flex-col items-center justify-center gap-1 z-20">
                        <div className="flex items-center gap-1 text-[8px] font-mono text-white">
                          <Heart className="w-2.5 h-2.5 text-white fill-white" />
                          <span>{post.hearts || `${post.id * 320}+`}</span>
                        </div>
                        <span className="text-[6px] font-mono text-white uppercase font-bold tracking-widest">
                          VIEW LOGS
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Instagram Feed Buttons — Load More + Follow */}
              {(() => {
                const hasMore = instagramPosts.length < totalCount;
                return (
                  <div className={`pt-1 font-mono text-[9px] grid gap-2 ${hasMore ? 'grid-cols-2' : 'grid-cols-1'}`}>
                    {hasMore && (
                      <button
                        type="button"
                        onClick={handleLoadMore}
                        disabled={isLoadingMore}
                        className="flex items-center justify-center gap-1.5 bg-white/10 hover:bg-white/20 disabled:opacity-60 text-white font-extrabold py-2.5 rounded-lg transition-all text-center uppercase tracking-wider cursor-pointer border border-white/15"
                      >
                        {isLoadingMore
                          ? <Loader2 size={10} className="animate-spin" />
                          : 'LOAD MORE'
                        }
                      </button>
                    )}
                    <a
                      href="https://www.instagram.com/cinescope.co.in"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="block bg-[#793bfb] hover:bg-[#8d52ff] text-white font-extrabold py-2.5 rounded-lg transition-all text-center uppercase tracking-wider cursor-pointer"
                    >
                      FOLLOW
                    </a>
                  </div>
                );
              })()}
            </div>
          </div>

        </div>

        {/* Bottom regulatory links and copyright */}
        <div className="pt-10 mt-16 border-t border-white/10 flex flex-col md:flex-row justify-between items-center gap-6 text-center md:text-left text-xs font-mono text-zinc-200 w-full">
          <div className="space-y-1.5">
            <p>© {new Date().getFullYear()} Cinescope India. All rights reserved.</p>
            <p className="text-[10px] text-zinc-300">
              Primary authorized distributor in India.
            </p>
            <div className="flex justify-center md:justify-start gap-3 text-[10px] text-zinc-400 mt-2">
              <Link to="/privacy" className="hover:text-white transition-colors">Privacy Policy</Link>
              <span>•</span>
              <Link to="/terms" className="hover:text-white transition-colors">Terms &amp; Conditions</Link>
            </div>
          </div>
          
           {/* <div className="text-[11px] font-mono text-zinc-400">
            Crafted with ❤️ by <br></br><span className="text-zinc-200 font-semibold">MultiTech Developers</span>
          </div> */}
        </div>

      </div>

      {/* Detail Instagram Post Viewport Lightbox Dialog Overlay (Rendered via React Portal) */}
      {createPortal(
        selectedInstaPost !== null && selectedPostData ? (
          <div className="fixed inset-0 z-[999] flex items-center justify-center p-4">
            {/* Light themed blurred backdrop */}
            <div
              onClick={() => setSelectedInstaPost(null)}
              className="absolute inset-0 bg-[#F4F7F9]/75 backdrop-blur-md transition-opacity"
            />

            {/* Lightbox Dialog Card */}
            <div
              className="relative w-full max-w-4xl bg-[#F4F7F9] border border-[#793bfb] rounded-2xl overflow-hidden shadow-2xl z-10 grid grid-cols-1 md:grid-cols-12 max-h-[90vh] text-zinc-900"
            >
              <button
                onClick={() => setSelectedInstaPost(null)}
                className="absolute top-4 right-4 z-50 p-2 rounded-full bg-white border border-[#793bfb] hover:bg-[#793bfb] text-zinc-700 transition-all cursor-pointer shadow-sm"
              >
                <X className="w-4 h-4" />
              </button>

              {/* Viewfinder Panel */}
              <div className="md:col-span-7 bg-black relative flex flex-col justify-center items-center min-h-[320px] border-b md:border-b-0 md:border-r border-[#793bfb] overflow-hidden text-white">
                  
                  {/* Media Content (Image or Video) */}
                  {selectedPostData.media_urls && selectedPostData.media_urls.length > 0 ? (
                    selectedPostData.type === 'video' ? (
                      <>
                        <video
                          src={getImageUrl(selectedPostData.media_urls[0])}
                          className="absolute inset-0 w-full h-full object-cover z-0"
                          autoPlay
                          loop
                          muted={isMuted}
                          playsInline
                        />
                        
                        {/* Custom Mute Toggle Button */}
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            setIsMuted(!isMuted);
                          }}
                          className="absolute bottom-12 right-4 z-20 bg-black/60 hover:bg-black/85 p-2 rounded-full text-white cursor-pointer hover:scale-105 transition-all flex items-center justify-center border border-white/10"
                          title={isMuted ? "Unmute Video" : "Mute Video"}
                        >
                          {isMuted ? <VolumeX size={14} /> : <Volume2 size={14} />}
                        </button>
                      </>
                    ) : (
                      <>
                        <img
                          src={getImageUrl(selectedPostData.media_urls[currentImageIndex])}
                          className="absolute inset-0 w-full h-full object-cover z-0"
                          alt={selectedPostData.title}
                        />
                        
                        {/* Image Slide Navigation */}
                        {selectedPostData.media_urls.length > 1 && (
                          <>
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                setCurrentImageIndex((prev) => (prev - 1 + selectedPostData.media_urls.length) % selectedPostData.media_urls.length);
                              }}
                              className="absolute left-4 top-1/2 -translate-y-1/2 z-20 bg-black/40 hover:bg-[#793bfb]/80 p-2 rounded-full text-white cursor-pointer hover:scale-105 transition-all"
                            >
                              <ChevronLeft size={16} />
                            </button>
                            
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                setCurrentImageIndex((prev) => (prev + 1) % selectedPostData.media_urls.length);
                              }}
                              className="absolute right-4 top-1/2 -translate-y-1/2 z-20 bg-black/40 hover:bg-[#793bfb]/80 p-2 rounded-full text-white cursor-pointer hover:scale-105 transition-all"
                            >
                              <ChevronRight size={16} />
                            </button>

                            {/* Dots */}
                            <div className="absolute bottom-12 left-1/2 -translate-x-1/2 z-20 flex gap-1.5 bg-black/35 backdrop-blur-xs px-2.5 py-1 rounded-full">
                              {selectedPostData.media_urls.map((_, dotIdx) => (
                                <button
                                  key={dotIdx}
                                  type="button"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setCurrentImageIndex(dotIdx);
                                  }}
                                  className={`w-1.5 h-1.5 rounded-full transition-all ${
                                    currentImageIndex === dotIdx ? 'bg-[#793bfb] scale-110' : 'bg-white/50'
                                  }`}
                                />
                              ))}
                            </div>
                          </>
                        )}
                      </>
                    )
                  ) : (
                    /* Fallback Mock Visual Screen if no media URLs are present */
                    <div className="p-8 text-center space-y-4 z-10">
                      <div className="w-16 h-16 rounded-full bg-white/10 border border-white/20 flex items-center justify-center mx-auto text-[#793bfb]">
                        {selectedPostData.type === 'video' ? <Play className="w-6 h-6 animate-pulse" /> : <Camera className="w-6 h-6" />}
                      </div>
                      <div>
                        <span className="font-mono text-[10px] text-zinc-400 uppercase tracking-widest">ACTIVE HARDWARE CAPTURE</span>
                        <h3 className="font-sans font-black text-white text-lg uppercase tracking-wide mt-1">
                          {selectedPostData.title}
                        </h3>
                      </div>
                    </div>
                  )}

                  {/* Viewfinder Overlays (Static grids and corners stay on top of media ONLY when type is video) */}
                  {selectedPostData.type === 'video' && (
                    <>
                      <div className="absolute inset-0 opacity-10 bg-[linear-gradient(to_right,#808080_1px,transparent_1px),linear-gradient(to_bottom,#808080_1px,transparent_1px)] bg-[size:20px_20px] pointer-events-none z-10" />
                      
                      <div className="absolute top-4 left-4 w-4 h-4 border-t-2 border-l-2 border-[#793bfb] pointer-events-none z-10" />
                      <div className="absolute top-4 right-4 w-4 h-4 border-t-2 border-r-2 border-[#793bfb] pointer-events-none z-10" />
                      <div className="absolute bottom-4 left-4 w-4 h-4 border-b-2 border-l-2 border-[#793bfb] pointer-events-none z-10" />
                      <div className="absolute bottom-4 right-4 w-4 h-4 border-b-2 border-r-2 border-[#793bfb] pointer-events-none z-10" />

                      <div className="absolute top-4 left-1/2 -translate-x-1/2 flex items-center gap-4 font-mono text-[9px] text-zinc-400 pointer-events-none z-10 bg-black/30 backdrop-blur-xs px-3 py-1 rounded-full">
                        <span>F_LEN: 40mm</span>
                        <span>T-STOP: T2.0</span>
                        <span className="text-[#793bfb] flex items-center gap-1">
                          <span className="w-1.5 h-1.5 bg-[#793bfb] rounded-full animate-ping" />
                          REC
                        </span>
                      </div>

                      <div className="absolute bottom-4 left-6 right-6 flex justify-between text-[8px] font-mono text-zinc-400 z-10 bg-black/30 backdrop-blur-xs px-3 py-1 rounded-full">
                        <span>FPS: 24.00</span>
                        <span>ISO: 800</span>
                      </div>
                    </>
                  )}
                </div>

                {/* Account Details Panel */}
                <div className="md:col-span-5 p-8 flex flex-col justify-between space-y-6 bg-[#F4F7F9] text-zinc-900">
                  <div className="space-y-4">
                    <div className="flex justify-between items-center border-b border-[#793bfb] pb-4 pr-8">
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 rounded-full bg-[#793bfb]/30 border border-[#793bfb] flex items-center justify-center text-[10px] font-mono font-bold text-zinc-800 shrink-0">
                          CS
                        </div>
                        <div className="flex flex-col">
                          <div className="flex items-center gap-1.5 flex-wrap">
                            <span className="text-[10px] font-bold font-mono lowercase">cinescope.co.in</span>
                            <span className="text-[7.5px] bg-[#793bfb]/10 text-[#793bfb] px-1.5 py-0.5 rounded font-mono font-extrabold uppercase tracking-wide shrink-0">
                              {formatDisplayDate(selectedPostData.date)}
                            </span>
                          </div>
                          <span className="text-[8px] text-zinc-500 font-mono leading-none mt-1">Specialist for Film &amp; TV gear</span>
                        </div>
                      </div>
                    </div>

                    <p className="text-xs text-zinc-700 font-sans leading-relaxed">
                      {selectedPostData.desc}
                    </p>

                    <div className="p-4 rounded-xl bg-white/60 border border-[#793bfb] text-xs italic text-zinc-700 shadow-inner">
                      "{selectedPostData.quote}"
                      <span className="block mt-2 font-mono text-[9px] not-italic text-[#793bfb] font-extrabold uppercase tracking-wide">
                        — Client review by {selectedPostData.client}
                      </span>
                    </div>
                  </div>

                  <div className="flex justify-between items-center border-t border-[#793bfb] pt-4">
                    <div className="flex items-center gap-2">
                      <Heart className="w-4 h-4 text-[#793bfb] fill-[#793bfb]" />
                      <span className="text-xs font-mono font-bold text-zinc-700">{selectedPostData.hearts} likes</span>
                    </div>
                    <span className="text-[10px] font-mono text-[#793bfb] font-extrabold uppercase tracking-wider">
                      Verified Node
                    </span>
                  </div>
                </div>
              </div>
            </div>
          ) : null,
          document.body
        )}

      {/* Newsletter Subscription Success Modal */}
      {createPortal(
        showSubscribeSuccess ? (
          <div className="fixed inset-0 z-[999] flex items-center justify-center p-4">
            {/* Blur Backdrop */}
            <div
              onClick={() => setShowSubscribeSuccess(false)}
              className="absolute inset-0 bg-[#0a0518]/80 backdrop-blur-md transition-opacity"
            />

            {/* Success Modal Card */}
            <div
              className="relative w-full max-w-md bg-gradient-to-br from-[#2b2344] via-[#1a152e] to-[#2b2344] border border-[#793bfb]/40 rounded-2xl p-8 shadow-[0_0_50px_rgba(121,59,251,0.25)] z-10 text-center text-white overflow-hidden transition-all"
            >
              {/* Visual scanner lines inside the card for high-tech cine feel */}
              <div className="absolute top-0 left-0 w-full h-[1px] bg-gradient-to-r from-transparent via-[#793bfb]/30 to-transparent animate-[shimmer_4s_infinite_linear]" />
              
              {/* Close Button X at Top Right */}
              <button
                onClick={() => setShowSubscribeSuccess(false)}
                className="absolute top-4 right-4 p-1.5 rounded-xl bg-white/5 border border-white/10 hover:bg-white/10 hover:border-white/20 text-zinc-400 hover:text-white transition-all cursor-pointer shadow-sm"
                aria-label="Close dialog"
              >
                <X className="w-4 h-4" />
              </button>

              {/* Animated Badge Icon */}
              <div className="relative w-16 h-16 mx-auto mb-6 flex items-center justify-center">
                <div 
                  className="absolute inset-0 rounded-full bg-[#793bfb]/10 border border-[#793bfb]/30"
                />
                <div 
                  className="w-12 h-12 rounded-full bg-gradient-to-br from-[#793bfb] to-[#5d24cc] flex items-center justify-center shadow-lg relative z-10"
                >
                  <Check className="w-6 h-6 text-white stroke-[3px]" />
                </div>
                
                {/* Floating sparkles */}
                <div 
                  className="absolute -top-1 -right-1 text-[#F6AE2D] animate-bounce"
                >
                  <Sparkles className="w-4 h-4" />
                </div>
              </div>

              {/* Heading */}
              <h3 className="font-display font-black text-xl md:text-2xl uppercase tracking-wider mb-3 text-white">
                Confirm Your Subscription!
              </h3>

              {/* Description */}
              <p className="font-sans text-xs text-zinc-300 leading-relaxed mb-6 max-w-sm mx-auto">
                We have sent a verification email to your address. Please click the "Confirm Your Subscription" link in that email to join our inner circle and start receiving exclusive updates.
              </p>

              {/* Action button */}
              <button
                onClick={() => setShowSubscribeSuccess(false)}
                className="w-full bg-[#793bfb] hover:bg-[#8d52ff] hover:shadow-[0_0_15px_rgba(121,59,251,0.3)] text-white font-mono font-bold text-xs uppercase tracking-widest py-3 rounded-xl transition-all cursor-pointer active:scale-98"
              >
                Continue Exploring
              </button>
            </div>
          </div>
        ) : null,
        document.body
      )}
    </footer>
  );
}
